<template>
  <div class="statistics">
    <Loading v-if="loading" />
    <div class="statistics__chooseDate">
      <div class="statistics__chooseDate__container">
        <div class="chooseDate-date-inputs">
          <div class="input">
            <Calendar
              type="datetime"
              format="DD/MM/YYYY HH:mm"
              :value="firstDay"
              name="startTime"
              :minute-step="selectedInterval"
              :hour-options="hourInterval"
              @change="changeDate"
            />
          </div>
          <img class="date-img" src="@/assets/icons/Hyphen.svg" alt="Hyphen" />
          <div class="input">
            <Calendar
              type="datetime"
              format="DD/MM/YYYY HH:mm"
              :value="today"
              name="endTime"
              @change="changeDate"
            />
          </div>
        </div>
      </div>
      <p v-if="!isDateCorrect" class="date-error">{{ $t('errorDate') }}</p>
    </div>
    <div class="statistics__chooseTemplates">
      <Dropdown
        class="statistics__templatesCharts-dropdown"
        :selected-option="getOptionTemplatesCharts.selectedOption"
        :options="getOptionTemplatesCharts.options"
        :multiple="true"
        @select-option="selectOptionTemplatesCharts"
      />
      <p v-if="!isDateCorrect" class="date-error">{{ $t('errorDate') }}</p>
    </div>
    <FeedbackMainTable
      :feedback-statistics="feedbackStatistics"
      :pickup-box-names="entityNames?.pickupBoxes"
      :is-date-correct="isDateCorrect"
    />
    <div v-if="feedbackStatistics?.labels?.length && isDateCorrect">
      <div class="statistics__intervals">
        <Dropdown
          class="statistics__intervals-dropdown"
          :selected-option="getOptionIntervals.selectedOption"
          :options="getOptionIntervals.options"
          @select-option="selectOptionIntervals"
        />
      </div>
      <FeedbackChartsDays
        :data-line-chart="dataLineChart"
        :data-bar-chart="dataBarChart"
      />
    </div>
    <div class="statistics__charts-week">
      <div class="week-title">
        <p>{{ $t('weekUpperCase') }}</p>
        <Button
          variant="bordered"
          :title="$t('showUpperCase')"
          @click="showWeekCharts"
        />
      </div>
      <div v-if="showBlockWeekCharts">
        <CustomersFeedbackWeek :pharmacy="pharmacy" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';
import FeedbackChartsDays from './FeedbackChartsDays/FeedbackChartsDays.vue';
import FeedbackMainTable from './FeedbackMainTable/FeedbackMainTable.vue';
import CustomersFeedbackWeek from './CustomersFeedbackWeek/CustomersFeedbackWeek.vue';

import router from '@/router';

import {
  columnsTemplates,
  activeOptions,
  defaultDataLineChart,
  defaultDataBarChart
} from '../configs';

import { InputFileEvent, Option } from '@/types';

@Options({
  name: 'Statistics',
  props: ['pharmacy'],
  components: {
    Loading,
    FeedbackChartsDays,
    FeedbackMainTable,
    CustomersFeedbackWeek
  },
  data() {
    return {
      pharmacyId: '',
      loading: false,
      changeOptions: false,
      getOptionIntervals: {
        selectedOption: {
          label: `${30} ${this.$t('minutes')}`,
          value: 30
        },
        options: activeOptions(this.$t)
      },
      getOptionTemplatesCharts: {
        selectedOption: [],
        options: {}
      },
      isInitialized: false,
      isFetching: false,
      dataLineChart: defaultDataLineChart,
      dataBarChart: defaultDataBarChart,
      selectedTemplatesString: '',
      selectedInterval: 30,
      hourInterval: 0,
      boxesOptions: null,
      optionTemplatesCharts: [],
      columnsTemplates: columnsTemplates(this.$t),
      showBlockWeekCharts: false,
      showDropdownSettingTemplates: false,
      params: {
        pharmacyId: '',
        startTime: new Date(
          new Date().setDate(new Date().getDate() - 7)
        ).setHours(0, 0, 0, 0),
        endTime: new Date().getTime(),
        minutesInterval: 30,
        chartFields: ''
      }
    };
  },
  created() {
    if (this.user) {
      this.pharmacyId = this.user?.pharmacyId;
      this.show();
    }
  },
  computed: {
    ...mapState('account', ['user']),
    ...mapState('entityNames', ['entityNames']),
    ...mapState('orders', ['feedbackStatistics']),
    today() {
      const todayDate = new Date().getTime();

      return todayDate;
    },
    firstDay(): number {
      const firstDate = new Date(
        new Date().setDate(new Date().getDate() - 7)
      ).setHours(0, 0, 0, 0);

      return firstDate;
    },
    isDateCorrect() {
      if (this.params && this.params.startTime && this.params.endTime) {
        return this.params.endTime >= this.params.startTime;
      }
      return true;
    }
  },
  updated() {
    this.optionTemplatesCharts = this.getOptionTemplatesCharts.selectedOption;
  },
  methods: {
    ...mapActions('entityNames', ['getEntityNames']),
    ...mapActions('orders', ['getFeedbackStatistics']),
    async show() {
      this.loading = true;
      await this.getEntityNames();

      if (this.pharmacy) {
        this.initOptionTemplatesCharts();
      }

      this.loading = false;
    },
    initOptionTemplatesCharts() {
      let allOptions = Object.keys(
        this.pharmacy.feedbackTemplate.options_screen.options
      ).map((key: string) => {
        const language = this.user?.appLanguage;
        const translations =
          this.pharmacy?.feedbackTemplate.options_screen.options[key]
            ?.translations;

        let label = key;

        if (
          translations &&
          Object.prototype.hasOwnProperty.call(translations, language)
        ) {
          label = translations[language] || translations['en'] || key;
        }

        return { value: key, label };
      });

      if (this.pharmacy.pickupBoxes.length && this.entityNames?.pickupBoxes) {
        const pickupBoxNames = this.entityNames.pickupBoxes;

        this.boxesOptions = this.pharmacy.pickupBoxes.map(
          (pickupBox: string) => ({
            value: pickupBox,
            label: pickupBoxNames[pickupBox],
            img: require('@/assets/icons/PickupBox.svg')
          })
        );

        allOptions = [...allOptions, ...this.boxesOptions];
      }

      this.getOptionTemplatesCharts.options = allOptions;

      this.getActiveCategory();
    },
    getActiveCategory() {
      if (this.pharmacy.remomediServices.feedback_tablet) {
        if (!this.changeOptions) {
          this.optionTemplatesCharts = Object.keys(
            this.pharmacy.feedbackTemplate.options_screen.options
          )
            .filter(
              (key) =>
                this.pharmacy.feedbackTemplate.options_screen.options[key]
                  .enabled
            )
            .map((key) => {
              const language = this.user?.appLanguage;
              const translations =
                this.pharmacy?.feedbackTemplate.options_screen.options[key]
                  ?.translations;

              let label = key;
              if (
                translations &&
                Object.prototype.hasOwnProperty.call(translations, language)
              ) {
                label = translations[language] || translations['en'] || key;
              }
              return { value: key, label };
            });
        }
      }

      if (
        this.pharmacy.pickupBoxes.length &&
        Array.isArray(this.boxesOptions)
      ) {
        this.optionTemplatesCharts.push(...this.boxesOptions);
      }

      this.optionTemplatesCharts = this.optionTemplatesCharts.filter(
        (opt: Option) =>
          !!this.getOptionTemplatesCharts.options.find(
            (o: Option) => o.value === opt.value
          )
      );
      this.getOptionTemplatesCharts.selectedOption = this.optionTemplatesCharts;

      this.getSelectedTemplates(this.optionTemplatesCharts);

      if (this.entityNames && !this.isInitialized) {
        this.params = {
          pharmacyId: this.pharmacyId,
          startTime: this.params.startTime,
          endTime: this.params.endTime,
          minutesInterval: this.selectedInterval,
          chartFields: this.selectedTemplatesString
        };
        this.isInitialized = true;
      }
    },
    getSelectedTemplates(options: Option[]) {
      const unique = Array.from(new Set(options.map((i) => i.value)));
      this.selectedTemplatesString = unique.join('__');
    },
    async initData() {
      if (this.isFetching) return;
      this.loading = true;

      const defaultParams = { ...this.params };

      if (
        defaultParams.startTime &&
        defaultParams.endTime &&
        this.isDateCorrect
      ) {
        await this.getFeedbackStatistics(defaultParams);

        const totalByTypes = this.feedbackStatistics.tableData.totalByTypes;
        Object.keys(totalByTypes).forEach((key) => {
          if (key !== 'total') {
            totalByTypes[key].label = key;
          } else {
            totalByTypes['total'].label = this.$t('total');
          }
        });

        this.initChartsData();
      }

      this.loading = false;
    },
    initChartsData() {
      if (this.feedbackStatistics?.labels?.length) {
        this.dataLineChart = {
          ...this.feedbackStatistics.chartDatasets,
          datasets: [
            {
              ...this.feedbackStatistics.chartDatasets.datasets[0],
              label: this.$t('happy'),
              data: Object.values(
                this.feedbackStatistics.chartDatasets.datasets[0].data
              )
            },
            {
              ...this.feedbackStatistics.chartDatasets.datasets[1],
              label: this.$t('ok'),
              data: Object.values(
                this.feedbackStatistics.chartDatasets.datasets[1].data
              )
            },
            {
              ...this.feedbackStatistics.chartDatasets.datasets[2],
              label: this.$t('angry'),
              data: Object.values(
                this.feedbackStatistics.chartDatasets.datasets[2].data
              )
            }
          ],
          labels: this.feedbackStatistics.labels
        };

        this.dataBarChart = {
          ...this.feedbackStatistics.diagramDatasets,
          datasets: [
            {
              ...this.feedbackStatistics.diagramDatasets.datasets[0],
              label: this.$t('numberOfUsers'),
              data: Object.values(
                this.feedbackStatistics.diagramDatasets.datasets[0].data
              ),
              backgroundColor: '#7f7f7f',
              borderWidth: 0
            }
          ],
          labels: this.feedbackStatistics.labels
        };
      }
    },
    selectOptionTemplatesCharts(option: Option) {
      this.getOptionTemplatesCharts.selectedOption = option;
    },
    selectOptionIntervals(option: Option) {
      this.getOptionIntervals.selectedOption = option;
    },
    changeDate(e: InputFileEvent) {
      const { name, value } = e.target;

      this.params = {
        ...this.params,
        [name]: value
      };
    },
    closeTimePanel() {
      this.initChartsData();
    },
    changeItemTemplates(option: Option) {
      this.getOptionTemplates.selectedOption = option;
    },
    showWeekCharts() {
      this.showBlockWeekCharts = !this.showBlockWeekCharts;
    },
    goToSettings() {
      router.push({ name: 'SettingsCustomersFeedback' });
    }
  },
  watch: {
    params: {
      deep: true,
      handler() {
        if (this.isDateCorrect) {
          this.dataLineChart = { ...defaultDataLineChart };
          this.dataBarChart = { ...defaultDataBarChart };
          this.initData();
        }
      }
    },
    'entityNames.pickupBoxes': {
      immediate: false,
      handler(value) {
        if (value) {
          this.initOptionTemplatesCharts();
        }
      }
    },
    pharmacy() {
      if (this.pharmacy) {
        this.pharmacyId = this.user?.pharmacyId;
        this.show();
      }
    },
    'getOptionTemplatesCharts.selectedOption': {
      immediate: true,
      handler(option: (Option | string)[]) {
        const validOptions = this.getOptionTemplatesCharts.options;

        const normalized = option.filter(
          (sel): sel is Option => typeof sel !== 'string'
        );

        this.optionTemplatesCharts = normalized.filter((sel) =>
          validOptions.some((v: Option) => v.value === sel.value)
        );

        this.getSelectedTemplates(this.optionTemplatesCharts);
      }
    },
    selectedTemplatesString() {
      if (this.feedbackStatistics?.labels?.length) {
        this.changeOptions = true;

        this.params = {
          ...this.params,
          chartFields: this.selectedTemplatesString
        };
      }
    },
    selectedInterval() {
      if (this.feedbackStatistics?.labels?.length) {
        this.params = {
          ...this.params,
          minutesInterval: this.selectedInterval
        };
      }
    },
    'getOptionIntervals.selectedOption': {
      immediate: true,
      handler(option: (Option | string)[]) {
        this.optionIntervals = option;
        this.selectedInterval = this.optionIntervals.value;

        if (this.optionIntervals.value === 60) {
          this.selectedInterval = 60;
          this.hourInterval = 60;
        }
      }
    }
  }
})
export default class Statistics extends Vue {}
</script>

<style lang="scss" scoped>
.statistics {
  .statistics__chooseDate {
    margin: 32px 24px;
    .statistics__chooseDate__container {
      display: flex;
      align-content: center;
      justify-content: space-between;
      .chooseDate-date-inputs {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        .formkit-outer {
          height: 42px;
          margin-bottom: 0 !important;
          .formkit-wrapper {
            height: 42px;
            .formkit-inner {
              height: 42px;
            }
          }
        }
        .input {
          position: relative;
          margin-bottom: 0;

          z-index: 2;
          .input-img {
            position: absolute;
            top: 12px;
            right: 12px;
            z-index: 0;
          }
        }
        .date-img {
          margin: 0 8px;
        }
      }
    }
    .date-error {
      color: $red;
      font-size: $fourteenSize;
      margin-top: 10px;
    }
  }

  .statistics__chooseTemplates {
    display: flex;
    align-content: center;
    justify-content: space-between;
    margin: 32px 24px;
    .chooseDate-date-inputs {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      .formkit-outer {
        height: 42px;
        margin-bottom: 0 !important;
        .formkit-wrapper {
          height: 42px;
          .formkit-inner {
            height: 42px;
          }
        }
      }
      .input {
        position: relative;
        margin-bottom: 0;

        z-index: 2;
        .input-img {
          position: absolute;
          top: 12px;
          right: 12px;
          z-index: 0;
        }
      }
      .date-img {
        margin: 0 8px;
      }
    }
    .statistics__templatesCharts-dropdown {
      height: 40px;
      width: 300px;
    }
    .statistics__settings {
      cursor: pointer;
    }
    .date-error {
      color: $red;
      font-size: $fourteenSize;
      margin-top: 10px;
    }
  }
  .statistics__intervals {
    margin: 32px 24px;

    .statistics__intervals-dropdown {
      height: 40px;
      width: 150px;
    }
  }
  .statistics__charts-week {
    position: relative;
    margin: 32px 0;
    .week-title {
      display: flex;
      align-items: center;
      justify-content: center;
      padding-top: 32px;
      p {
        font-size: $eighteenSize;
        font-family: 'Ubuntu-Medium';
        font-weight: 400;
        margin-right: 12px;
      }
    }
    &::before {
      position: absolute;
      content: '';
      top: 0;
      left: 50%;
      width: 100%;
      height: 1px;
      background-color: $backgroundBefore;
      transform: translateX(-50%);
    }
  }
}
</style>
